// Única fuente de importes, cuotas y prueba gratuita del marketing 2.0 (D-18). Antes cada superficie escribía sus
// propios precios en JSX y podían divergir; ahora todas leen este módulo:
//   - SeccionPrecios (landing #precios) y la página /precios, incluida su tabla comparativa;
//   - el Offer de JsonLd.tsx, a través de PRECIO_DESDE_SCHEMA;
//   - precios-coherencia-stripe.test.ts, que rechaza importes obsoletos;
//   - el test del backend limiteStarterPublicado, que lee la primera coincidencia de /sesionesMes:\s*(\d+)/ y la
//     compara con LIMITE_SESIONES_STARTER. Por eso la cuota de Starter es un literal numérico y es la única.
// Procedencia (D-35): los importes son los de Stripe en producción; Stripe solo tiene precios mensuales con IVA
// (D-17), así que no se publica otra periodicidad. Las cuotas de preguntas salen de config/limitesAsistente.js (LIMITES_PREGUNTAS free 63, basic 127,
// premium 319). Los miembros de una clínica viva cuentan como premium (Q-1) y quien está en prueba como free (Q-2),
// según resolverPlanAsistente en services/asistente/planAsistente.service.js. La prueba de 10 sesiones y
// 10 documentos es la de middleware/trialLimits.middleware.js.

// El campo se llama `simbolo` y no `icono`: subset-material-symbols trata cualquier `icono: '<nombre>'` como un
// glifo de la fuente Material Symbols, y estos son los SVG propios de la tarjeta, no glifos de la fuente.
export type SimboloCuota = 'microfono' | 'chat';

export interface LineaCuota {
    readonly simbolo: SimboloCuota;
    readonly destacado: string;
    readonly resto: string;
}

export interface PlanIndividual {
    readonly id: 'starter' | 'professional';
    readonly nombre: string;
    readonly descripcion: string;
    readonly importeMensual: number;
    readonly precio: string;
    readonly precioAnterior: string | null;
    readonly sesionesMes: number | null;
    readonly preguntasAsistenteMes: number;
    readonly etiquetaDestacada: string | null;
    readonly lineasCuota: readonly LineaCuota[];
    readonly enlaceRegistro: string;
}

export interface PlanClinica {
    readonly id: 'clinic_3' | 'clinic_5';
    readonly nombre: string;
    readonly descripcion: string;
    readonly importeMensual: number;
    readonly precio: string;
    readonly licencias: number;
    readonly preguntasAsistenteMes: number;
    readonly porProfesional: string;
    readonly enlaceRegistro: string;
}

export interface ElementoIncluido {
    readonly titulo: string;
    readonly texto: string;
}

export interface ElementoPrueba {
    readonly numero: number;
    readonly texto: string;
}

export interface PruebaGratuita {
    readonly dias: number;
    readonly sesiones: number;
    readonly documentos: number;
    readonly preguntasAsistente: number;
    readonly elementos: readonly ElementoPrueba[];
}

export const SUFIJO_PRECIO = '/ mes, IVA incl.';

// Formato español de un importe: los enteros sin decimales (149 €), el resto con dos y coma (29,90 €).
function formatearImporte(importe: number): string {
    return Number.isInteger(importe) ? String(importe) : importe.toFixed(2).replace('.', ',');
}

// Las cifras «Sale a X € por profesional» se calculan para que no puedan desviarse del importe de la clínica.
export function precioPorProfesional(importe: number, licencias: number): string {
    if (!Number.isInteger(licencias) || licencias <= 0) {
        throw new Error(
            `precioPorProfesional: las licencias deben ser un entero positivo (importe=${importe}, licencias=${licencias})`,
        );
    }
    return formatearImporte(Math.round((importe / licencias) * 100) / 100);
}

type DatosPlanIndividual = Omit<PlanIndividual, 'lineasCuota'>;

// Las líneas de cuota se derivan de los campos numéricos: el texto de la tarjeta no puede contradecir la cuota.
function conLineasDeCuota(plan: DatosPlanIndividual): PlanIndividual {
    const sesiones: LineaCuota = plan.sesionesMes === null
        ? { simbolo: 'microfono', destacado: 'Sesiones sin límite', resto: ' mensual' }
        : { simbolo: 'microfono', destacado: `${plan.sesionesMes} sesiones`, resto: ' al mes' };
    const preguntas: LineaCuota = {
        simbolo: 'chat',
        destacado: `${plan.preguntasAsistenteMes} preguntas`,
        resto: ' al asistente al mes',
    };
    return { ...plan, lineasCuota: [sesiones, preguntas] };
}

const PLAN_STARTER = conLineasDeCuota({
    id: 'starter',
    nombre: 'Starter',
    descripcion: 'Para una consulta con pocas sesiones al mes.',
    importeMensual: 29.9,
    precio: '29,90 €',
    precioAnterior: null,
    sesionesMes: 40,
    preguntasAsistenteMes: 127,
    etiquetaDestacada: null,
    enlaceRegistro: '/app/register',
});

const PLAN_PROFESSIONAL = conLineasDeCuota({
    id: 'professional',
    nombre: 'Professional',
    descripcion: 'Para una consulta activa, sin tope de sesiones.',
    importeMensual: 39.9,
    precio: '39,90 €',
    precioAnterior: '59,90 €',
    sesionesMes: null,
    preguntasAsistenteMes: 319,
    etiquetaDestacada: 'Recomendado si usas el asistente a diario',
    enlaceRegistro: '/app/register',
});

export const PLANES_INDIVIDUALES: readonly PlanIndividual[] = [PLAN_STARTER, PLAN_PROFESSIONAL];

// Cada licencia de clínica tiene la cuota de Professional (Q-1).
export const PLANES_CLINICA: readonly PlanClinica[] = [
    {
        id: 'clinic_3',
        nombre: 'Clínica · 3 profesionales',
        descripcion: 'Para un gabinete pequeño que trabaja en equipo.',
        importeMensual: 149,
        precio: '149 €',
        licencias: 3,
        preguntasAsistenteMes: PLAN_PROFESSIONAL.preguntasAsistenteMes,
        porProfesional: precioPorProfesional(149, 3),
        enlaceRegistro: '/app/register?plan=clinic_3',
    },
    {
        id: 'clinic_5',
        nombre: 'Clínica · 5 profesionales',
        descripcion: 'Para un centro con varias consultas.',
        importeMensual: 230,
        precio: '230 €',
        licencias: 5,
        preguntasAsistenteMes: PLAN_PROFESSIONAL.preguntasAsistenteMes,
        porProfesional: precioPorProfesional(230, 5),
        enlaceRegistro: '/app/register?plan=clinic_5',
    },
];

// El Offer de schema.org quiere el importe como texto con punto y dos decimales ('29.90'), no como número.
export const PRECIO_DESDE_SCHEMA = PLAN_STARTER.importeMensual.toFixed(2);

// 17-29 (auditoría SEO 2.0, schema M-1): los cuatro planes mensuales publicados, en el orden de /precios, para el
// AggregateOffer del JSON-LD. Salen de los mismos objetos que pintan las tarjetas, así que no pueden divergir.
export const OFERTAS_SCHEMA: readonly { nombre: string; precio: string }[] = [...PLANES_INDIVIDUALES, ...PLANES_CLINICA].map(
    (plan) => ({ nombre: plan.nombre, precio: plan.importeMensual.toFixed(2) }),
);

// «Psicometría» lleva el texto de contrato C-1 (D-23): la prueba llega ya corregida y el terapeuta confirma.
export const INCLUIDO_EN_LOS_DOS: readonly ElementoIncluido[] = [
    { titulo: 'Transcripción', texto: 'de cada sesión, con su análisis clínico.' },
    { titulo: 'Notas de progreso', texto: 'en SOAP, DAP, BIRP, GIRP o PIRP.' },
    { titulo: 'Asistente con 11 funciones', texto: 'para analizar, redactar y organizar.' },
    { titulo: 'Tablero del paciente', texto: 'y mapa de todos tus pacientes.' },
    { titulo: 'Informes por secciones', texto: 'en Word o PDF, y siete plantillas clínicas.' },
    { titulo: 'Documentos del paciente', texto: 'con cronología, carpetas y fechas.' },
    {
        titulo: 'Psicometría:',
        texto: 'subes la prueba ya corregida, confirmas sus puntuaciones y el asistente describe los cambios.',
    },
    { titulo: 'Plan de tratamiento', texto: 'con propuestas que tú apruebas.' },
    { titulo: 'Datos en Europa,', texto: 'con contrato de encargado de tratamiento (DPA).' },
];

const DATOS_PRUEBA = { dias: 7, sesiones: 10, documentos: 10, preguntasAsistente: 63 } as const;

export const PRUEBA_GRATUITA: PruebaGratuita = {
    ...DATOS_PRUEBA,
    elementos: [
        { numero: DATOS_PRUEBA.dias, texto: 'días de prueba, sin tarjeta' },
        { numero: DATOS_PRUEBA.sesiones, texto: 'sesiones para transcribir' },
        { numero: DATOS_PRUEBA.documentos, texto: 'documentos del paciente' },
        { numero: DATOS_PRUEBA.preguntasAsistente, texto: 'preguntas al asistente' },
    ],
};
